/** Shared, bounded ERP read cache. Business writes invalidate reads and snapshots. */
const RETAIN_MS = 5 * 60_000;
const MAX_ENTRIES = 100;
type Entry = { value: unknown; savedAt: number };
type Pending = { controller: AbortController; promise: Promise<unknown>; users: number };
const entries = new Map<string, Entry>();
const pending = new Map<string, Pending>();
let scope: string | null = null;

function abortError() { return new DOMException('Request cancelled', 'AbortError'); }
function ensureScope() {
    const next = localStorage.getItem('supplier_portal_session');
    if (next !== scope) { clearQueryCache(); scope = next; }
}
export function clearQueryCache(fragment?: string) {
    for (const key of Array.from(entries.keys())) {
        if (!fragment || key.includes(fragment)) entries.delete(key);
    }
    for (const [key, request] of Array.from(pending.entries())) {
        if (!fragment || key.includes(fragment)) {
            pending.delete(key);
            request.controller.abort();
        }
    }
}
export function peekQuery<T>(key: string, maxAge = RETAIN_MS): T | undefined {
    ensureScope();
    const entry = entries.get(key);
    if (!entry) return undefined;
    if (Date.now() - entry.savedAt >= RETAIN_MS) { entries.delete(key); return undefined; }
    if (Date.now() - entry.savedAt >= maxAge) return undefined;
    entries.delete(key); entries.set(key, entry);
    return entry.value as T;
}
export function putQuery<T>(key: string, value: T) {
    ensureScope();
    entries.delete(key); entries.set(key, { value, savedAt: Date.now() });
    while (entries.size > MAX_ENTRIES) entries.delete(entries.keys().next().value!);
}

/** Subscribers share a GET; leaving one page does not cancel another subscriber. */
export function readQuery<T>(key: string, load: (signal: AbortSignal) => Promise<T>,
    options: { signal?: AbortSignal; staleTime?: number } = {}): Promise<T> {
    ensureScope();
    if (options.signal?.aborted) return Promise.reject(abortError());
    const cached = peekQuery<T>(key, options.staleTime ?? 30_000);
    if (cached !== undefined) return Promise.resolve(cached);
    let request = pending.get(key);
    if (!request) {
        const controller = new AbortController();
        const created: Pending = { controller, users: 0, promise: Promise.resolve() };
        let timedOut = false;
        const timer = setTimeout(() => { timedOut = true; controller.abort(); }, 30_000);
        created.promise = Promise.resolve().then(() => load(controller.signal)).then(value => {
            ensureScope();
            if (controller.signal.aborted || pending.get(key) !== created) throw abortError();
            putQuery(key, value);
            return value;
        }).catch(error => {
            if (timedOut) throw new Error('请求超时，请重试');
            throw error;
        }).finally(() => {
            clearTimeout(timer);
            if (pending.get(key) === created) pending.delete(key);
        });
        pending.set(key, created);
        request = created;
    }
    const shared = request;
    shared.users++;
    return new Promise<T>((resolve, reject) => {
        let done = false;
        const finish = (action: () => void) => {
            if (done) return;
            done = true; options.signal?.removeEventListener('abort', cancelled);
            shared.users--;
            if (shared.users === 0 && pending.get(key) === shared) {
                pending.delete(key); shared.controller.abort();
            }
            action();
        };
        const cancelled = () => finish(() => reject(abortError()));
        options.signal?.addEventListener('abort', cancelled, { once: true });
        shared.promise.then(value => finish(() => resolve(value as T)), error => finish(() => reject(error)));
    });
}
